import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useCallback, useMemo, useState, useEffect } from 'react';

import { confirmToast, successToast, errorToast } from '@/common/toast';
import { DataGrid } from '@/components/common';
import { useGroupList } from '@/hooks/groups.hook';
import { useMapUserToGroups } from '@/hooks/manager.hook';
import { DateCell, GroupCourseCell } from '@/components/pages/components';

/**
 * 그룹 추가 모달 컴포넌트
 * 
 * 전체 그룹 목록을 조회하고 매니저에게 추가할 수 있는 모달입니다.
 * 체크박스로 다중 선택하여 한 번에 그룹을 추가/제거할 수 있습니다.
 * 
 * @param {Object} props
 * @param {boolean} props.open - 모달 열림 상태
 * @param {Function} props.onClose - 모달 닫기 핸들러
 * @param {string} props.userId - 매니저 사용자 ID
 * @param {Array} props.currentGroupIds - 현재 매니저가 속한 그룹 ID 배열
 * @param {Function} props.onSuccess - 추가 성공 시 콜백
 */
const AddGroupModal = ({
    open,
    onClose,
    userId,
    currentGroupIds = [],
    onSuccess,
}) => {
    // 검색 상태
    const [searchKeyword, setSearchKeyword] = useState('');

    // 실제 쿼리용 상태
    const [queryOptions, setQueryOptions] = useState({
        search: ''
    });

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });

    // 선택된 그룹 ID (MUI DataGrid v8 형식: { type: 'include', ids: Set })
    const [rowSelectionModel, setRowSelectionModel] = useState({
        type: 'include',
        ids: new Set(),
    });

    // 그룹 매핑 mutation
    const mapUserToGroupsMutation = useMapUserToGroups();

    // 그룹 목록 조회
    const { data: groupListData, isLoading: isGroupListLoading } = useGroupList({
        page: paginationModel.page + 1,
        size: paginationModel.pageSize,
        search: queryOptions.search,
    });

    // 그룹 목록
    const groupListRows = groupListData?.list || [];
    const groupListTotal = groupListData?.total || 0;

    const [rowCountState, setRowCountState] = useState(0);
    useEffect(() => {
        if (!isGroupListLoading) setRowCountState(groupListTotal);
    }, [groupListTotal, isGroupListLoading]);

    // 모달이 열릴 때 현재 그룹 ID들을 초기 선택으로 설정
    useEffect(() => {
        if (open) {
            setRowSelectionModel({
                type: 'include',
                ids: new Set(currentGroupIds),
            });
        }
    }, [open, currentGroupIds]);

    // 검색 실행
    const handleSearch = useCallback(() => {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        setQueryOptions({
            search: searchKeyword,
        });
    }, [searchKeyword]);

    // 검색 초기화
    const handleReset = useCallback(() => {
        setSearchKeyword('');
        setQueryOptions({ search: '' });
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, []);

    // 선택된 그룹들을 적용하는 핸들러
    const handleApply = useCallback(() => {
        const selectedIds = rowSelectionModel?.type === 'include'
            ? [...(rowSelectionModel.ids ?? new Set())]
            : [];
        const addedCount = selectedIds.filter(id => !currentGroupIds.includes(id)).length;
        const removedCount = currentGroupIds.filter(id => !selectedIds.includes(id)).length;

        let message = '';
        if (addedCount > 0 && removedCount > 0) {
            message = `추가 ${addedCount}개, 제거 ${removedCount}개의 그룹 변경을 적용하시겠습니까?`;
        } else if (addedCount > 0) {
            message = `선택한 ${addedCount}개의 그룹을 추가하시겠습니까?`;
        } else if (removedCount > 0) {
            message = `선택 해제한 ${removedCount}개의 그룹을 제거하시겠습니까?`;
        } else {
            errorToast('변경사항이 없습니다.');
            return;
        }

        confirmToast(
            message,
            async () => {
                try {
                    await mapUserToGroupsMutation.mutateAsync({
                        userId,
                        groupIds: selectedIds,
                    });
                    successToast('그룹이 성공적으로 적용되었습니다.');
                    onSuccess?.();
                    onClose();
                } catch (error) {
                    errorToast(`그룹 적용에 실패했습니다. - ${error.response?.data?.msg}`);
                }
            }
        );
    }, [rowSelectionModel, currentGroupIds, userId, mapUserToGroupsMutation, onSuccess, onClose]);

    // 그룹 컬럼 정의
    const columns = useMemo(() => [
        {
            field: 'groupId',
            headerName: '그룹ID',
            flex: 1,
            minWidth: 100,
            sortable: false,
            type: 'number',
        },
        {
            field: 'groupName',
            headerName: '고객사명',
            flex: 1,
            minWidth: 150,
            sortable: false,
        },
        {
            field: 'groupCourse',
            headerName: '그룹명',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => <GroupCourseCell row={params.row} />,
        },
        {
            field: 'groupStartDate',
            headerName: '시작일',
            flex: 0.5,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => <DateCell row={params.row} dateField="groupStartDate" />,
        },
        {
            field: 'groupDueDate',
            headerName: '종료일',
            flex: 0.5,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => <DateCell row={params.row} dateField="groupDueDate" />,
        },
    ], []);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
                그룹 추가
            </DialogTitle>
            <DialogContent>
                {/* 검색 필터 */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2} sx={{ mt: 2 }}>
                    <Stack direction="row" gap={1} alignItems="center">
                        <TextField
                            size='small'
                            placeholder='그룹명 또는 고객사명 검색'
                            value={searchKeyword}
                            onChange={(e) => setSearchKeyword(e.target.value)}
                            onKeyUp={(e) => e.key === 'Enter' && handleSearch()}
                            sx={{ width: 250 }}
                        />

                        <Button variant='contained' onClick={handleSearch} sx={{ height: 40 }}>검색</Button>
                        <Button variant='outlined' onClick={handleReset} sx={{ height: 40 }}>초기화</Button>
                    </Stack>
                    <Typography variant='body2' color='text.secondary'>
                        선택된 그룹: {rowSelectionModel.ids.size}개
                    </Typography>
                </Stack>

                {/* 그룹 데이터 그리드 */}
                <DataGrid
                    rows={groupListRows ?? []}
                    columns={columns}
                    loading={isGroupListLoading}
                    rowCount={rowCountState}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    getRowId={(row) => row.groupId}
                    checkboxSelection
                    disableRowSelectionExcludeModel
                    rowSelectionModel={rowSelectionModel}
                    onRowSelectionModelChange={(newSelection) => {
                        setRowSelectionModel(newSelection ?? { type: 'include', ids: new Set() });
                    }}
                    disableRowSelectionOnClick={false}
                    height={500}
                    sx={{
                        '& .MuiDataGrid-row': {
                            cursor: 'pointer',
                        },
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>취소</Button>
                <Button 
                    variant='contained' 
                    onClick={handleApply}
                    disabled={mapUserToGroupsMutation.isPending}
                >
                    {mapUserToGroupsMutation.isPending ? '적용 중...' : '적용'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddGroupModal;
